import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaGoogle, FaApple } from 'react-icons/fa';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import { signInWithOAuth } from './oauth';
import { beginGuestUpgrade } from './guestSession';
import { confirmedGuestEmailSession } from './guestEmailVerification';
import { authErrorParams } from './authErrors';
import { appUrl } from './appNavigation';
import { getAuthRedirectPath, getAuthRedirectUrl } from './authRedirect';
import { useI18n } from './i18n';
import './auth-pages.css';
import './guest.css';
import { useGuestCopy } from './guestCopy';
import { trackEvent, trackGuestUpgradeStart, trackGuestUpgradeAccountVerified, trackGuestUpgradeCredentialsReady } from './analytics';
import { trackAuthEvent } from './authAttribution';
import { clearGuestEmailSetup, guestUpgradeErrorType, rememberGuestEmailSetup } from './guestUpgrade';
import { getBillingPriceSummary } from './pricing';

export default function GuestAccountUpgrade({ finishEmail = false, autoProvider = null, embedded = false,
  redirectPath = null, onComplete, onCancel, onMethod, freeAccountOnly = false }) {
  const { currentUser } = useAuth();
  const copy = useGuestCopy();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const authState = redirectPath ? { from: redirectPath, freeAccountOnly } : location.state;
  const authRedirectPath = getAuthRedirectPath(authState);
  const premiumInterval = new URL(authRedirectPath, window.location.origin).searchParams.get('premium');
  const premiumIntent = ['monthly', 'annual'].includes(premiumInterval);
  const price = getBillingPriceSummary(premiumInterval);
  const [email, setEmail] = useState(currentUser?.email || '');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [confirmationNotice, setConfirmationNotice] = useState('');
  const [confirmedUser, setConfirmedUser] = useState(null);
  const running = useRef(false);
  const setupUser = confirmedUser || currentUser;
  const verified = (finishEmail || emailConfirmed) && setupUser && !setupUser.is_anonymous && setupUser.email_confirmed_at;
  const run = async (action, method, stage) => {
    if (running.current) return;
    running.current = true;
    setBusy(true); setError(''); setErrorType(''); setConfirmationNotice('');
    try { await action(); } catch (e) {
      const type = guestUpgradeErrorType(e);
      trackEvent('guest_upgrade_error', { method, ...authErrorParams(e, stage) });
      const recovery = { account_exists: copy.recoveryExists, rate_limit: copy.recoveryRate, expired: copy.recoveryExpired, auth: copy.recoveryAuth, email: copy.recoveryEmail, email_delivery: copy.recoveryEmailDelivery, password: copy.recoveryPassword, network: copy.recoveryNetwork, timeout: copy.recoveryNetwork };
      setErrorType(type);
      setError(recovery[type] || copy.verifyError);
    }
    finally { running.current = false; setBusy(false); }
  };
  const oauth = (provider) => run(async () => {
    onMethod?.(provider);
    trackGuestUpgradeStart(provider);
    trackAuthEvent('signup_attempt', { method: provider, source: 'guest_upgrade' });
    // The existing verified handover supports both a new OAuth account and an
    // existing one. linkIdentity would reject an already-used Google identity.
    await signInWithOAuth({ provider, options: { redirectTo: getAuthRedirectUrl(authState, { auth_target: 'signup', auth_method: provider }) } });
  }, provider, 'oauth_start');
  const started = useRef(false);
  useEffect(() => {
    if (autoProvider === 'google' && currentUser?.is_anonymous && !started.current) {
      started.current = true;
      oauth('google');
    }
    // One explicit landing-page Google click starts one OAuth attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoProvider, currentUser?.is_anonymous]);
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (verified) {
        const { error: failure } = await supabase.auth.updateUser({ password });
        if (failure) throw failure;
        clearGuestEmailSetup();
        trackGuestUpgradeCredentialsReady(setupUser);
        if (onComplete) onComplete();
        else navigate(authRedirectPath, { replace: true });
      } else {
        onMethod?.('email');
        trackGuestUpgradeStart('email', currentUser?.id);
        trackAuthEvent('signup_attempt', { method: 'email', source: 'guest_upgrade' });
        await beginGuestUpgrade();
        const { error: failure } = await supabase.auth.updateUser({ email: email.trim() }, {
          emailRedirectTo: appUrl(`/signup?finish_guest_email=1&redirect_path=${encodeURIComponent(authRedirectPath)}`),
        });
        if (failure) throw failure;
        rememberGuestEmailSetup(currentUser?.id);
        trackEvent('guest_upgrade_email_sent', { method: 'email' });
        setSent(true);
        setEditingEmail(false);
        // Keep the recovery screen mounted if Supabase discards a guest token
        // that was rotated by verification in another browser.
        if (!embedded) navigate(`/signup?finish_guest_email=1&redirect_path=${encodeURIComponent(authRedirectPath)}`, { replace: true });
      }
    }, 'email', verified ? 'password_setup' : 'email_send');
  };
  return <div className={embedded ? 'guest-upgrade-inline' : 'auth-container'}><section className="auth-card">
    {!embedded && <div className="auth-header"><h1>{verified ? copy.finishTitle : copy.upgradeTitle}</h1>
      <p>{copy.upgradeSubtitle}</p></div>}
    {embedded && verified && <h3>{copy.finishTitle}</h3>}
    {premiumIntent && <p className="guest-upgrade-hint">{t('billing.signupPremiumHint')}<br />
      <strong>{t(premiumInterval === 'annual' ? 'billing.annual' : 'billing.monthly')} · {price.monthlyDisplayPrice}{t('billing.perMonth')}</strong><br />
      {t(premiumInterval === 'annual' ? 'billing.billedAnnual' : 'billing.billedMonthly', { annualTotal: price.annualTotalPrice })}
    </p>}
    {!embedded && <div className="guest-upgrade-benefits">{copy.durable}<br />{copy.devices}<br />{copy.premium}</div>}
    {error && <p role="alert" className="auth-error">{error}</p>}
    {(sent || (finishEmail && !verified && !editingEmail)) ? <>
      <p role="status" className="auth-success">{sent ? copy.sent : copy.confirmationNeeded}</p>
      <button type="button" className="auth-btn secondary-btn" disabled={busy} onClick={() => run(async () => {
        const session = await confirmedGuestEmailSession();
        if (!session) { setConfirmationNotice(copy.emailPending); return; }
        setConfirmedUser(session.user);
        trackGuestUpgradeAccountVerified(session.user);
        setEmailConfirmed(true);
        setSent(false);
        if (!embedded) navigate(`/signup?finish_guest_email=1&redirect_path=${encodeURIComponent(authRedirectPath)}`, { replace: true });
      }, 'email', 'email_verification')}>{busy ? copy.waiting : copy.confirmedEmail}</button>
      {confirmationNotice && <p className="guest-upgrade-hint" role="status">{confirmationNotice}</p>}
      {sent && errorType !== 'auth' && <button type="button" className="auth-text-link" disabled={busy} onClick={() => { setSent(false); setEditingEmail(true); setError(''); setConfirmationNotice(''); }}>{copy.changeEmail}</button>}
    </> : <>
      {!verified && <>
        <button disabled={busy} className="auth-btn google-btn" onClick={() => oauth('google')}><FaGoogle /> {copy.google}</button>
        <button disabled={busy} className="auth-btn secondary-btn" onClick={() => oauth('apple')}><FaApple /> {copy.apple}</button>
        <div className="auth-divider">{copy.or}</div>
      </>}
      <form className="auth-form" onSubmit={submit}>
        {verified ? <label className="guest-upgrade-label">{copy.password}<input className="form-control" type="password" autoComplete="new-password" minLength={8} value={password} onChange={e => setPassword(e.target.value)} required /></label>
          : <label className="guest-upgrade-label">{copy.email}<input className="form-control" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required /></label>}
        <button disabled={busy} className="auth-btn primary-btn" type="submit">{busy ? copy.waiting : verified ? copy.savePassword : copy.sendEmail}</button>
      </form>
    </>}
    <p className="guest-upgrade-hint">{errorType === 'auth' ? copy.recoverySignin : copy.existing} <Link to="/login" state={{ from: authRedirectPath, freeAccountOnly, email: email.trim(), showEmailLogin: Boolean(email.trim()), finishGuestEmail: errorType === 'auth' }} onClick={() => errorType === 'auth' ? trackEvent('guest_upgrade_recovery_start', { method: 'email', guest_stage: 'email_verification' }) : trackGuestUpgradeStart('existing_account')}>{copy.signin}</Link>.</p>
    {embedded ? <button type="button" className="auth-text-link" onClick={onCancel}>{copy.back}</button> : <Link to="/dashboard">{copy.back}</Link>}
  </section></div>;
}
